import {
  useRef,
  useEffect,
  Fragment,
} from 'react';
import type {
  MouseEvent,
} from 'react';
import {
  navigate,
} from 'vike/client/router';
import {
  usePageContext,
} from 'vike-react/usePageContext';
import {
  usePriceDrops,
  useAuthentication,
} from '@dinosave/core/stores';
import type {
  UserEntity,
} from '@dinosave/core/types';
import BackButton from '@/components/BackButton';
import AuthenticationModal from '@/components/Modals/AuthenticationModal';
import PriceDropsModal from '@/components/Modals/PriceDropsModal';
import UserImage from '@/components/Users/UserImage';
import {
  AUTH,
} from '@/constants/namespaces';
import useModal from '@/hooks/useModal';
import Button from '@/ui/components/Button';
import Skeleton from '@/ui/components/Skeleton';
import Text from '@/ui/components/Text';
import {
  TagIcon,
  GearIcon,
} from '@/ui/icons';

import styles from './Header.module.css';

type HeaderProps = {
  withLogo?: boolean;
  withBack?: boolean;
  withActions?: boolean;
  withNavigation?: boolean;
}

type HeaderNavigationLink = {
  href: string;
  label: string;
}

const Header = (props: HeaderProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    withBack = false,
    withLogo = false,
    withActions = false,
    withNavigation = false,
  } = props;

  // ##########################################################################
  // PARAMS
  // ##########################################################################

  const {
    urlParsed,
  } = usePageContext();
  const {
    pathname,
  } = urlParsed;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const authLoading = useAuthentication(s => s.authLoading);
  const authenticatedUser = useAuthentication(s => s.authenticatedUser);
  const authenticatedUserSlug = authenticatedUser?.slug;

  const priceDropsUnseen = usePriceDrops(AUTH, s => s.priceDropsUnseen) || 0;
  const priceDropsUnseenLoading = usePriceDrops(AUTH, s => s.priceDropsUnseenLoading);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const headerNavigationRef = useRef(null);
  const headerNavigationToggleRef = useRef(null);
  const headerNavigationToggle = headerNavigationToggleRef.current;

  const priceDropsModal = useModal();
  const authenticationModal = useModal();

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  let homePathname = '/';
  if (
    authenticatedUser
  ) homePathname = `/u/${authenticatedUserSlug}`;

  const isFeedPathname = [
    '/feed',
  ].includes(pathname);

  const isHomePathname = [
    `/u/${authenticatedUserSlug}`,
  ].includes(pathname);

  const isPublicPathname = [
    '/',
    '/terms',
    '/privacy',
  ].includes(pathname);

  const isFriendsPathname = [
    '/friends/accepted',
    '/friends/received',
  ].includes(pathname);

  const navigation: HeaderNavigationLink[] = [{
    href: homePathname,
    label: 'Home',
  }, {
    href: '/friends/accepted',
    label: 'Friends',
  }];

  const showBack = !withLogo && (!authenticatedUser || withBack);

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleNavigation = (e: MouseEvent<HTMLAnchorElement>) => {
    const {
      offsetLeft,
      offsetWidth,
    } = e.currentTarget;

    const scaleX = (offsetWidth + 4) / 2;
    const translateX = offsetLeft;
    headerNavigationToggle.style.transform = `translateX(${translateX}px) scaleX(${scaleX})`;

    if (
      authenticatedUser
    ) return;

    e.preventDefault();
    authenticationModal.toggle();
  };

  const handleAuthentication = (user: UserEntity) => {
    if (
      !isPublicPathname
    ) return;

    navigate(`/u/${user.slug}`);
  };

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    const headerNavigation = headerNavigationRef.current;
    const headerNavigationToggle = headerNavigationToggleRef.current;

    if (
      !headerNavigation ||
      !headerNavigationToggle
    ) return;

    const isNavigationPath = isHomePathname ||
                             isFeedPathname ||
                             isFriendsPathname;

    if (
      !isNavigationPath
    ) {
      headerNavigationToggle.style.transform = null;
      return;
    }

    let headerNavigationLink = headerNavigation.children[0];

    if (
      isFriendsPathname
    ) headerNavigationLink = headerNavigation.children[1];

    if (
      isFeedPathname
    ) headerNavigationLink = headerNavigation.children[2];

    const {
      offsetLeft,
      offsetWidth,
    } = headerNavigationLink;

    const scaleX = (offsetWidth + 4) / 2;
    const translateX = offsetLeft;
    headerNavigationToggle.style.transform = `translateX(${translateX}px) scaleX(${scaleX})`;
  }, [
    isFeedPathname,
    isHomePathname,
    isFriendsPathname,
    headerNavigationRef,
    headerNavigationToggleRef,
  ]);

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <Fragment>
      <PriceDropsModal {...priceDropsModal} />

      <AuthenticationModal
        {...authenticationModal}
        onSuccess={handleAuthentication}
      />

      <header className={styles.header}>
        {withNavigation && (
          <div
            ref={headerNavigationRef}
            className={styles.navigation}
          >
            {navigation.map((link) => (
              <a
                key={link.label}
                href={link.href}
                onClick={handleNavigation}
                className={styles.link}
              >
                <Button
                  kind="link"
                  color="secondary"
                  centered
                >
                  <Text
                    kind="control"
                    size="regular"
                  >
                    {link.label}
                  </Text>
                </Button>
              </a>
            ))}

            <div
              ref={headerNavigationToggleRef}
              className={styles.underline}
            />
          </div>
        )}

        {showBack && (
          <div className={styles.back}>
            <BackButton />
          </div>
        )}

        {withLogo && (
          <div className={styles.logo}>
            <BackButton />
          </div>
        )}

        {withActions && (
          <div className={styles.actions}>
            {authLoading && (
              <Skeleton roundness="round" />
            )}

            {!authenticatedUser && !authLoading && (
              <div className={styles.auth}>
                <Button
                  kind="default"
                  onClick={authenticationModal.toggle}
                  roundness="round"
                  centered
                >
                  <div className={styles.authText}>
                    <Text
                      kind="control"
                      size="regular"
                    >
                      Get Started
                    </Text>
                  </div>
                </Button>
              </div>
            )}

            {authenticatedUser && (
              <Fragment>
                <div className={styles.notifications}>
                  <Button
                    kind="ghost"
                    color="secondary"
                    onClick={priceDropsModal.toggle}
                    loading={priceDropsUnseenLoading}
                    disabled={priceDropsUnseenLoading}
                    centered
                  >
                    <TagIcon />

                    {!!priceDropsUnseen && (
                      <div className={styles.indicator} />
                    )}
                  </Button>
                </div>
                <div className={styles.settings}>
                  <a href="/settings">
                    <Button
                      kind="ghost"
                      color="secondary"
                      centered
                    >
                      <GearIcon />
                    </Button>
                  </a>
                </div>
                <div className={styles.profile}>
                  <a href={`/u/${authenticatedUser?.slug}`}>
                    <Button
                      kind="ghost"
                      color="secondary"
                      roundness="round"
                      centered
                    >
                      <div className={styles.user}>
                        <UserImage
                          user={authenticatedUser}
                          roundness="round"
                        />
                      </div>
                    </Button>
                  </a>
                </div>
              </Fragment>
            )}
          </div>
        )}
      </header>
    </Fragment>
  );
};

export default Header;
